import { useCallback, useRef, useState, type ReactNode } from 'react';
import { AUDIO_VOLUME_IDS, CHARACTER_HOME_EFFECTS, type AudioVolumeId, type CharacterHomeEffects, type DisplaySettingId } from '@sorrem/shared';
import { Button, Icon, Popover, Slider, ToggleRow, type PopoverPlacement } from '@sorrem-client/ui/components';
import {
    formatBindingLabel,
    labelWithKey,
    useAudioSettings,
    useCameraZoom,
    useDisplaySettings,
    useInputBindings,
    useReplayTours,
    useResetCallouts,
} from '@sorrem-client/ui/hooks';
import { useOpenBugReport } from '@sorrem-client/ui/features/BugReport';
import { useDeveloperSnapshot } from '@sorrem-client/ui/features/DeveloperSnapshot';
import { CameraZoomRow } from './CameraZoomRow';
import { VolumeRow } from './VolumeRow';
import styles from './SettingsPopover.module.scss';
import { toTourTargetProps } from '@sorrem-client/ui/shared/tour-targets';

interface SettingsPopoverProps {
    /** Which side of the gear the pop-over opens on: below a header, above a HUD button. */
    placement: PopoverPlacement;
    /** More sections after the account ones, e.g. the in-run Developer section. */
    children?: ReactNode;
    /** Adds a Logout button at the bottom, where nothing else offers one (in a run). */
    onLogout?: () => void;
    /** Optional extra class on the gear's wrapper. */
    className?: string;
}

const COMBAT_SETTINGS: ReadonlyArray<{ id: DisplaySettingId; label: string }> = [
    { id: 'showEnemyEssenceBars', label: 'Show enemy essence bars' },
    { id: 'showPartyEssenceBars', label: 'Show party member essence bars' },
    { id: 'showOwnEssenceBar', label: 'Show your own essence bar' },
    { id: 'showPartyDamageNumbers', label: 'Show party damage numbers' },
];

/** Each Character Home effects level as the slider names it. */
const CHARACTER_HOME_EFFECTS_LABELS: Readonly<Record<CharacterHomeEffects, string>> = {
    off: 'Off',
    minimal: 'Minimal',
    all: 'All',
};

const VOLUME_LABELS: Readonly<Record<AudioVolumeId, string>> = {
    master: 'Master',
    music: 'Music',
    ambience: 'Ambience',
    effects: 'Effects',
    ui: 'Interface',
};

/**
 * The settings gear and its pop-over, the same in Character Home and in a
 * run (#203): the account's display settings, saved to the account, then any
 * sections the caller adds. Its button opens the bug report (#218), which
 * the app root hosts (`BugReportHost`).
 */
export function SettingsPopover({ placement, children, onLogout, className }: SettingsPopoverProps): JSX.Element {
    const anchorRef = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);
    const close = useCallback((): void => setOpen(false), []);
    const openReportForm = useOpenBugReport();
    const openBugReport = useCallback((): void => {
        setOpen(false);
        openReportForm();
    }, [openReportForm]);
    const { characterHomeEffects, displaySettings, setCharacterHomeEffects, setDisplaySetting } = useDisplaySettings();
    const effectsStep = CHARACTER_HOME_EFFECTS.indexOf(characterHomeEffects);
    const cameraZoom = useCameraZoom();
    const { audioSettings, previewVolume, setVolume, setMuted } = useAudioSettings();
    const callouts = useResetCallouts();
    const tours = useReplayTours();
    const snapshot = useDeveloperSnapshot();
    const { bindings } = useInputBindings();
    // Closes the pop-over and lets two frames paint without it, so it isn't in the picture.
    const takeAfterClosing = useCallback((take: () => void): void => {
        setOpen(false);
        requestAnimationFrame(() => requestAnimationFrame(take));
    }, []);

    return (
        <>
            <div ref={anchorRef} className={className} {...toTourTargetProps('settings')}>
                <Icon
                    icon="settings"
                    tooltip="Settings"
                    onClick={() => setOpen((wasOpen) => !wasOpen)}
                    aria-expanded={open}
                    aria-haspopup="dialog"
                />
            </div>
            <Popover open={open} anchorRef={anchorRef} placement={placement} onClose={close}>
                <div className={styles.settingsPopover}>
                    <section className={styles.section} aria-labelledby="settings-display-title">
                        <h2 id="settings-display-title" className={styles.sectionTitle}>Display</h2>
                        {/* Minimal holds the 3D home and character still and keeps the 2D sky and skill tree moving. */}
                        <div className={styles.stepRow}>
                            <label className={styles.stepLabel} htmlFor="setting-character-home-effects">Character Home effects</label>
                            <Slider
                                id="setting-character-home-effects"
                                min={0}
                                max={CHARACTER_HOME_EFFECTS.length - 1}
                                step={1}
                                value={effectsStep}
                                aria-valuetext={CHARACTER_HOME_EFFECTS_LABELS[characterHomeEffects]}
                                onChange={(event) => {
                                    const next = CHARACTER_HOME_EFFECTS[Number(event.target.value)];
                                    if (next !== undefined) {
                                        setCharacterHomeEffects(next);
                                    }
                                }}
                            />
                            <span className={styles.volumeValue}>{CHARACTER_HOME_EFFECTS_LABELS[characterHomeEffects]}</span>
                        </div>
                    </section>
                    <section className={styles.section} aria-labelledby="settings-camera-title">
                        <h2 id="settings-camera-title" className={styles.sectionTitle}>Camera</h2>
                        <CameraZoomRow
                            id="setting-camera-zoom"
                            level={cameraZoom.cameraZoom.level}
                            onPreview={cameraZoom.previewLevel}
                            onCommit={cameraZoom.setLevel}
                        />
                        <ToggleRow
                            className={styles.toggleRow}
                            id="setting-camera-zoom-locked"
                            label="Lock zoom"
                            checked={cameraZoom.cameraZoom.isLocked}
                            onChange={cameraZoom.setLocked}
                        />
                    </section>
                    <section className={styles.section} aria-labelledby="settings-combat-title">
                        <h2 id="settings-combat-title" className={styles.sectionTitle}>Combat</h2>
                        {COMBAT_SETTINGS.map(({ id, label }) => (
                            <ToggleRow
                                key={id}
                                className={styles.toggleRow}
                                id={`setting-${id}`}
                                label={label}
                                checked={displaySettings[id]}
                                onChange={(checked) => setDisplaySetting(id, checked)}
                            />
                        ))}
                    </section>
                    <section className={styles.section} aria-labelledby="settings-sound-title">
                        <h2 id="settings-sound-title" className={styles.sectionTitle}>Sound</h2>
                        <ToggleRow
                            className={styles.toggleRow}
                            id="setting-audio-muted"
                            label="Mute all sound"
                            checked={audioSettings.isMuted}
                            onChange={setMuted}
                        />
                        {AUDIO_VOLUME_IDS.map((id) => (
                            <VolumeRow
                                key={id}
                                id={`setting-volume-${id}`}
                                label={VOLUME_LABELS[id]}
                                volume={audioSettings[id]}
                                onPreview={(volume) => previewVolume(id, volume)}
                                onCommit={(volume) => setVolume(id, volume)}
                            />
                        ))}
                    </section>
                    <section className={styles.section} aria-labelledby="settings-tips-title">
                        <h2 id="settings-tips-title" className={styles.sectionTitle}>Tips</h2>
                        <p className={styles.note}>First-time tips you closed or chose not to see again, and the guided tours.</p>
                        <Button variant="secondary" className={styles.wideButton} onClick={callouts.reset} disabled={!callouts.hasHidden}>
                            Show all tips again
                        </Button>
                        <Button variant="secondary" className={styles.wideButton} onClick={tours.replay} disabled={!tours.hasProgress}>
                            Replay tours
                        </Button>
                    </section>
                    {snapshot.isAvailable && (
                        <section className={styles.section} aria-labelledby="settings-snapshot-title">
                            <h2 id="settings-snapshot-title" className={styles.sectionTitle}>Snapshot</h2>
                            <Button variant="secondary" className={styles.wideButton} onClick={() => takeAfterClosing(snapshot.takeSnapshot)}>
                                {labelWithKey('Take snapshot', formatBindingLabel(bindings, 'take-snapshot'))}
                            </Button>
                            <Button variant="secondary" className={styles.wideButton} onClick={() => takeAfterClosing(snapshot.takeCleanRender)}>
                                {labelWithKey('Clean render', formatBindingLabel(bindings, 'take-clean-render'))}
                            </Button>
                        </section>
                    )}
                    {children}
                    <Button variant="secondary" className={styles.reportButton} onClick={openBugReport}>
                        Report a bug
                    </Button>
                    {onLogout !== undefined && (
                        <Button variant="secondary" className={styles.logoutButton} onClick={onLogout}>
                            Logout
                        </Button>
                    )}
                </div>
            </Popover>
        </>
    );
}
